import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

import { cn } from '@/lib/cn';
import { useHaptics } from '@/hooks/useHaptics';

import { stepDotsFor } from '../constants';
import type { StepId, WizardState } from '../types';
import { ProgressBar } from './ProgressBar';
import { PreviewSidebar } from './PreviewSidebar';

interface Props {
  state: WizardState;
  children: ReactNode;
  /** Disable the Next button (e.g. step invalid). */
  nextDisabled?: boolean;
  /** Override "Next" label — used on action-firing steps. */
  nextLabel?: string;
  /** Show a spinner inside Next (mutation in flight). */
  nextPending?: boolean;
  onBack?: () => void;
  onNext?: () => void;
  onClose: () => void;
}

export function EditorShell({
  state,
  children,
  nextDisabled,
  nextLabel,
  nextPending,
  onBack,
  onNext,
  onClose,
}: Props) {
  const haptics = useHaptics();
  const dots = stepDotsFor(state.sourceMode);
  const stepIdx = dots.findIndex(d => d.id === effectiveDotStep(state.currentStep, dots));
  const stepCount = dots.length;
  const stepLabel = stepIdx >= 0 ? dots[stepIdx].label : 'Confirm';

  return (
    <div className="flex h-dvh flex-col bg-bg">
      {/* Header */}
      <header className="flex items-center justify-between gap-3 px-4 pt-4">
        <button
          type="button"
          aria-label="Close editor"
          onClick={() => { haptics.impact('light'); onClose(); }}
          className="rounded-full bg-bg-secondary p-2 text-fg transition-opacity active:opacity-80"
        >
          <X aria-hidden className="h-4 w-4" />
        </button>
        <div className="flex flex-1 flex-col items-center">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
            Step {stepIdx + 1} of {stepCount} · {stepLabel}
          </p>
        </div>
        <div className="w-8" aria-hidden />
      </header>

      <div className="px-4 pt-3 pb-1">
        <ProgressBar current={state.currentStep} sourceMode={state.sourceMode} />
      </div>

      {/* Body: two-column on wide viewports, stacked on mobile */}
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pt-4 pb-4 md:flex-row">
        <main className="flex flex-1 flex-col gap-4">{children}</main>
        <PreviewSidebar
          state={state}
          className="hidden h-fit w-64 shrink-0 md:flex"
        />
      </div>

      {/* Sticky footer */}
      <footer className="flex shrink-0 items-center gap-2 border-t border-border bg-bg/95 px-4 py-3 backdrop-blur pb-[env(safe-area-inset-bottom)]">
        <button
          type="button"
          onClick={() => { haptics.impact('light'); onBack?.(); }}
          disabled={!onBack}
          className={cn(
            'flex items-center gap-1 rounded-xl bg-bg-secondary px-3 py-3 text-sm font-semibold transition-opacity active:opacity-80',
            !onBack && 'opacity-30',
          )}
        >
          <ChevronLeft aria-hidden className="h-4 w-4" />
          Back
        </button>
        <button
          type="button"
          onClick={() => { haptics.impact('medium'); onNext?.(); }}
          disabled={!onNext || nextDisabled || nextPending}
          className={cn(
            'flex flex-1 items-center justify-center gap-1 rounded-xl py-3 text-sm font-semibold transition-opacity active:opacity-80',
            (!nextDisabled && !nextPending && onNext)
              ? 'bg-brand text-brand-fg'
              : 'bg-bg-secondary text-fg-muted',
          )}
        >
          {nextPending ? (
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <>
              <span>{nextLabel ?? 'Next'}</span>
              <ChevronRight aria-hidden className="h-4 w-4" />
            </>
          )}
        </button>
      </footer>
    </div>
  );
}

function effectiveDotStep(s: StepId, dots: ReadonlyArray<{ id: StepId; label: string }>): StepId {
  if (s !== 'confirm') return s;
  return dots[dots.length - 1].id;
}
